// "The portal has moved" (Jill 2026-10-22). Shown in place of the whole app
// when it is opened at the old address (see onRetiredAddress). A redirect at
// Vercel cannot move these pilots on its own: the home screen copy answers
// page loads from the phone, so it never reaches the redirect. This screen
// reaches them through the normal update banner instead.
//
// It first saves the device's unfinished drafts to the pilot's account (so
// they open at the new address), keeps the outbox draining, and says plainly
// what is still only on this phone. The move button is never disabled: a
// pilot who must go now can, with the warning in front of them.
// There is no way back into the old app from here (Jill 2026-10-30: the
// season is over, nobody is mid-slip on the old address); anything the
// copy cannot move is started again at seapaportal.com.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, PageHeader, Button } from "../ui";
import theme from "../theme";
import seapaLogo from "../assets/SeapaLogo.png";
import { useAuth } from "../contexts/AuthContext";
import { computeSyncSummary } from "../lib/syncSummary";
import { drainSubmitQueue } from "../lib/submitQueue";
import { supabase } from "../supabaseClient";
import { NEW_PORTAL_URL, carryDraftsToAccount, voyagePlanDeviceOnly } from "../lib/portalMove";

const T = theme.tokens;
const list = { margin: "6px 0 0", paddingLeft: 20 };

function Section({ title, children }) {
  return (
    <Card style={{ padding: "16px 18px", marginBottom: 14 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: T.text.lg, color: T.ink, fontWeight: 700 }}>{title}</h2>
      <div style={{ color: T.textMuted, fontSize: T.text.md, lineHeight: 1.55 }}>{children}</div>
    </Card>
  );
}

function StatusLine({ tone, children }) {
  const color = tone === "ok" ? "var(--success-fg)" : tone === "warn" ? "var(--warn-fg)" : T.textMuted;
  return <div style={{ marginTop: 6, color, fontWeight: tone ? 600 : 400 }}>{children}</div>;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Who has reached this screen and how it went, for the check before the old
// address is retired. Best effort, once per screen load.
function logArrival(carry) {
  try {
    const msg = [
      `carry=${carry.status}`, `total=${carry.total}`, `carried=${carry.carried}`,
      `safe=${carry.alreadySafe}`, `failed=${carry.failed}`, `unmatched=${carry.unmatched}`, `notes=${carry.notesSaved}`, `sig=${carry.signatureSaved ? 1 : 0}`, `filed=${carry.filedSaved}`,
      `waiting=${computeSyncSummary().waiting}`, navigator.userAgent,
    ].join(" | ").slice(0, 900);
    supabase.from("seapa_debug_log").insert({ voyage_number: null, step: "portal_moved_screen", msg }).then(() => {}, () => {});
  } catch { /* the log is a bonus */ }
}

export function PortalMovedScreen() {
  const { user, session, loading } = useAuth();
  const userId = user?.id || session?.user?.id || null;
  const [carry, setCarry] = useState(null); // null while checking
  const [sync, setSync] = useState(computeSyncSummary);
  const [plan, setPlan] = useState(voyagePlanDeviceOnly);
  const logged = useRef(false);

  const runCarry = useCallback(async () => {
    setCarry(null);
    drainSubmitQueue();
    let res;
    try {
      // Signed out still runs: the device's drafts are counted, never
      // declared clear (see carryDraftsToAccount).
      res = await carryDraftsToAccount(userId);
    } catch {
      res = { status: "unreachable", total: 0, carried: 0, alreadySafe: 0, failed: 1, unmatched: 0, notesSaved: 0 };
    }
    setCarry(res);
    setSync(computeSyncSummary());
    setPlan(voyagePlanDeviceOnly());
    if (!logged.current && userId) { logged.current = true; logArrival(res); }
  }, [userId]);

  // Wait for the login to settle, or a signed-in pilot reads as signed out.
  useEffect(() => { if (!loading) runCarry(); }, [loading, runCarry]);

  // The outbox sends by itself when the signal returns; keep the count live.
  useEffect(() => {
    const update = () => { setSync(computeSyncSummary()); setPlan(voyagePlanDeviceOnly()); };
    const onOnline = () => { drainSubmitQueue(); update(); };
    window.addEventListener("submit-queue-changed", update);
    window.addEventListener("online", onOnline);
    const iv = setInterval(update, 10000);
    return () => {
      window.removeEventListener("submit-queue-changed", update);
      window.removeEventListener("online", onOnline);
      clearInterval(iv);
    };
  }, []);

  const carryProblem = carry && ["partial", "unreachable", "no-session"].includes(carry.status);
  const allClear = carry && !carryProblem && !sync.waiting && !plan.routes;

  const saved = carry ? carry.carried + carry.alreadySafe : 0;
  const carryLines = [];
  if (!carry) {
    carryLines.push(<StatusLine key="checking">Saving this device&rsquo;s drafts to your account&hellip;</StatusLine>);
  } else if (carry.status === "nothing") {
    carryLines.push(<StatusLine key="none" tone="ok">No unfinished slips or expense reports on this device.</StatusLine>);
  } else if (carry.status === "no-session") {
    carryLines.push(
      <StatusLine key="nosession" tone="warn">
        Your sign-in on this device could not be confirmed, so its {plural(carry.total, "draft", "drafts")} could not be
        saved to your account. Connect to the internet and tap Try again. If that does not work, sign in at
        seapaportal.com and start {carry.total === 1 ? "it" : "them"} again there.
      </StatusLine>
    );
  } else {
    if (saved > 0) carryLines.push(
      <StatusLine key="saved" tone="ok">
        {plural(saved, "unfinished draft is", "unfinished drafts are")} saved to your account. They will be waiting for you at seapaportal.com.
      </StatusLine>
    );
    if (carry.notesSaved > 0) carryLines.push(
      <StatusLine key="notes" tone="ok">
        {plural(carry.notesSaved, "voyage plan note is", "voyage plan notes are")} saved to your account.
      </StatusLine>
    );
    if (carry.filedSaved > 0) carryLines.push(
      <StatusLine key="filed" tone="ok">
        The full copy of {plural(carry.filedSaved, "submitted slip is", "submitted slips are")} now in your account.
      </StatusLine>
    );
    if (carry.signatureSaved) carryLines.push(
      <StatusLine key="sig" tone="ok">Your saved signature is in your account.</StatusLine>
    );
    if (carry.failed > 0) carryLines.push(
      <StatusLine key="failed" tone="warn">
        {plural(carry.failed, "item", "items")} could not be saved to your account yet. Connect to the internet and tap Try again.
      </StatusLine>
    );
    if (carry.unmatched > 0) carryLines.push(
      <StatusLine key="unmatched" tone="warn">
        {plural(carry.unmatched, "draft", "drafts")} on this device could not be matched to your account (another
        pilot&rsquo;s, or a report made out to someone else), so {carry.unmatched === 1 ? "it was" : "they were"} not moved.
        If {carry.unmatched === 1 ? "it is" : "they are"} yours, start {carry.unmatched === 1 ? "it" : "them"} again at seapaportal.com.
      </StatusLine>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--page)" }}>
      <header style={{
        display: "flex", alignItems: "center", gap: 10, background: T.navBg, color: T.navFg,
        padding: "8px 14px", boxShadow: theme.shadows.navbar,
      }}>
        <img src={seapaLogo} alt="SEAPA Logo" style={{ height: 32 }} />
        <span style={{ fontWeight: 600, fontSize: "1rem", whiteSpace: "nowrap" }}>SEAPA Portal</span>
      </header>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "18px 16px 40px", textAlign: "left" }}>
        <PageHeader
          title="The portal has moved"
          subtitle="The portal now lives at seapaportal.com. This old address is being retired on December 1, 2026."
        />

        <Section title="Your work on this device">
          {carryLines}
          {sync.waiting > 0 && (
            <StatusLine tone="warn">
              {plural(sync.waiting, "item is", "items are")} still only on this device: a slip waiting to send, a receipt
              photo waiting to upload, or slip edits not yet saved. Stay connected here and they send by themselves.
              A receipt photo or an edit that does not send must be done again at seapaportal.com.
            </StatusLine>
          )}
          {plan.routes > 0 && (
            <StatusLine tone="warn">
              {plural(plan.routes, "route choice", "route choices")} made without a signal {plan.routes === 1 ? "has" : "have"} not
              been shared yet and {plan.routes === 1 ? "does" : "do"} not move. Make {plan.routes === 1 ? "that choice" : "those choices"} again at seapaportal.com.
            </StatusLine>
          )}
          {plan.watchSettings > 0 && (
            <StatusLine>
              Watch calculator settings (who, watch lengths, handover moves) are kept on this device and do not move.
              Watches you posted are shared and safe; set the calculator up again at seapaportal.com if you still need it.
            </StatusLine>
          )}
          {carry && sync.waiting === 0 && !plan.routes && !carryProblem && carry.status !== "nothing" && (
            <StatusLine tone="ok">Nothing is waiting to send from this device.</StatusLine>
          )}
          {carry && (carryProblem || sync.waiting > 0 || plan.routes > 0) && (
            <div style={{ marginTop: 12 }}>
              <Button variant="outline" onClick={runCarry}>Try again</Button>
            </div>
          )}
        </Section>

        <Section title="Move to seapaportal.com">
          {!allClear && (
            <div style={{ marginBottom: 10, color: "var(--warn-fg)", fontWeight: 600 }}>
              {carry
                ? "Anything above that is not yet saved to your account, or still waiting on this device, will not come with you."
                : "Wait for the check above to finish. Anything not yet saved to your account will not come with you."}
            </div>
          )}
          <a
            href={NEW_PORTAL_URL}
            style={{
              display: "inline-block", background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)",
              padding: "12px 20px", borderRadius: 8, fontWeight: 700, fontSize: T.text.lg, textDecoration: "none",
            }}
          >
            Open seapaportal.com
          </a>
        </Section>
        <Section title="Signing in the first time">
          <ul style={list}>
            <li>You will sign in again the first time at seapaportal.com. That is expected. Use your usual email and password.</li>
            <li>
              If the password does not work, tap <strong>Request link to create or reset password</strong> on the login page.
              Open the email and tap the link <strong>once</strong>. Do not long-press or preview it: the link works one time only,
              and a preview uses it up. Set a new password on the page that opens.
            </li>
          </ul>
        </Section>
        <Section title="Remove the old icon from your home screen">
          <ul style={list}>
            <li>
              <strong>iPhone or iPad:</strong> touch and hold the old SEAPA icon until a menu appears, choose the delete choice
              (it reads Delete Bookmark, Delete App or Remove, depending on the device), then confirm.
            </li>
            <li><strong>Android:</strong> touch and hold the old icon, then drag it to Remove or tap Uninstall.</li>
          </ul>
        </Section>
        <Section title="Put the new one on your home screen">
          <ul style={list}>
            <li>
              <strong>iPhone or iPad:</strong> open seapaportal.com in Safari, tap the <strong>Share</strong> button (the square with
              an arrow pointing up), scroll down and tap <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.
            </li>
            <li>
              <strong>Android:</strong> open seapaportal.com in Chrome, tap the three dots at the top right, tap{" "}
              <strong>Add to Home screen</strong>, then tap <strong>Add</strong>.
            </li>
            <li>The new icon is labelled SEAPA Portal. Open the portal from that icon from now on.</li>
          </ul>
        </Section>

      </div>
    </div>
  );
}
